import { Card, Image, Button, Tooltip, Badge } from 'antd';
import { CopyOutlined, FileImageOutlined } from '@ant-design/icons';
import type { MediaRow } from '../../api/client';
import { fmtTaipei } from '../article-list/status';
import { useIsMobile } from '../../hooks/useIsMobile';

/** 顯示用檔名：優先 title；遷移資料 title 為空時取 URL 末段並還原 percent-encoding。 */
export function displayName(m: { title: string | null; url: string }): string {
  if (m.title) return m.title;
  const last = m.url.split('/').pop() ?? m.url;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

interface Props {
  item: MediaRow;
  onCopy: (url: string) => void;
  onOpen: (item: MediaRow) => void;
}

/** 媒體庫單張卡片：縮圖 + 刊出時間 + 被幾篇文章用過。 */
export default function MediaCard({ item, onCopy, onOpen }: Props) {
  const name = displayName(item);
  const when = item.publishedAt ?? item.createdAt;
  // 手機：卡片窄，省掉「刊出／上傳」字樣與複製鈕（點縮圖開的詳情抽屜裡就有複製網址）
  const mobile = useIsMobile();
  return (
    <Card
      size="small"
      hoverable
      styles={{ body: { padding: mobile ? 6 : 8 } }}
      cover={
        <div className="aspect-square cursor-pointer overflow-hidden bg-gray-50" onClick={() => onOpen(item)}>
          <Image
            src={item.url}
            alt={item.alt ?? name}
            width="100%"
            height="100%"
            preview={false}
            style={{ objectFit: 'cover' }}
          />
        </div>
      }
    >
      <div className={`truncate text-gray-600 ${mobile ? 'text-[12px]' : 'text-xs'}`} title={name}>
        {name}
      </div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="text-[11px] text-gray-400">
          {!mobile && (item.publishedAt ? '刊出 ' : '上傳 ')}
          {fmtTaipei(when).slice(0, 10)}
        </span>
        <span className="flex items-center gap-1">
          {item.articleCount > 0 && (
            <Tooltip title={`被 ${item.articleCount} 篇文章使用`}>
              <Badge count={item.articleCount} size="small" color="#bfbfbf" offset={[0, 0]}>
                <FileImageOutlined className="text-gray-300" />
              </Badge>
            </Tooltip>
          )}
          {!mobile && (
            <Tooltip title="複製網址">
              <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onCopy(item.url)} />
            </Tooltip>
          )}
        </span>
      </div>
    </Card>
  );
}
